import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";
import CrudTable from "../components/CrudTable";
import PDFButton from "../components/PDFButton";
import LifecycleTab from "../components/LifecycleTab";
import MonthlyReadings from "../components/MonthlyReadings";
import { Modal, FileCCE, Empty } from "../components/ui";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, Legend, ComposedChart, Line, LineChart, Area } from "recharts";
import { Droplets } from "lucide-react";

const TABS = [
  { key: "wells", label: "Pozos de Agua CONAGUA" },
  { key: "ptar", label: "PTAR" },
  { key: "energy", label: "Energía" },
  { key: "fuels", label: "Combustibles" },
  { key: "raw_materials", label: "Consumo de materia prima" },
  { key: "chemicals", label: "Sustancias químicas" },
  { key: "air", label: "Aire — Emisiones" },
  { key: "soil", label: "Suelo" },
  { key: "biodiversity", label: "Biodiversidad y Suelos" },
  { key: "lifecycle", label: "Ciclo de Vida" },
  { key: "other", label: "Otro" },
];

const CFE_FACTOR = 0.432 / 1000.0; // kg CO2/KWH → TON
const DIESEL_FACTOR = 2.65 / 1000.0; // kg CO2/L → TON
const GASOLINE_FACTOR = 2.31 / 1000.0;

// Map navigation state tab aliases (Spanish) to internal tab keys
const TAB_ALIASES = {
  pozos: "wells", wells: "wells",
  ptar: "ptar",
  energia: "energy", energy: "energy",
  combustibles: "fuels", fuels: "fuels",
  "materia-prima": "raw_materials", "raw-materials": "raw_materials",
  "sustancias-quimicas": "chemicals", chemicals: "chemicals",
  aire: "air", air: "air",
  suelo: "soil", soil: "soil",
  biodiversidad: "biodiversity", biodiversity: "biodiversity",
  "ciclo-vida": "lifecycle", lifecycle: "lifecycle",
  otro: "other", other: "other",
};

export default function Resources() {
  const location = useLocation();
  const initialTab = TAB_ALIASES[location?.state?.tab] || "wells";
  const [tab, setTab] = useState(initialTab);
  useEffect(() => {
    const t = TAB_ALIASES[location?.state?.tab];
    if (t) setTab(t);
  }, [location?.state?.tab]);
  return (
    <div data-testid="resources-module">
      <div className="flex items-center justify-between mb-3">
        <h1 className="card-h1" style={{ margin: 0 }}>Consumo de Recursos (Agua, PTAR, Energía)</h1>
        <PDFButton endpoint="/pdf/conagua" filename="reporte_conagua.pdf" label="Reporte CONAGUA" testId="conagua-pdf" />
      </div>
      <div className="tabs-bar">
        {TABS.map((t) => (
          <button key={t.key} className={`tab ${tab === t.key ? "active" : ""}`} onClick={() => setTab(t.key)} data-testid={`res-tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === "wells" && <Wells />}
      {tab === "ptar" && <PTAR />}
      {tab === "energy" && <EnergyTab />}
      {tab === "fuels" && <FuelsTab subFromNav={location?.state?.sub} />}
      {tab === "raw_materials" && <RawMaterialsTab />}
      {tab === "chemicals" && <ChemicalsTab />}
      {tab === "air" && <AirTab />}
      {tab === "soil" && <SoilTab />}
      {tab === "biodiversity" && <BiodiversityTab />}
      {tab === "lifecycle" && <LifecycleTab />}
      {tab === "other" && <OtherIndicatorsTab />}
    </div>
  );
}

function Wells() {
  const [wells, setWells] = useState([]);
  const [selected, setSelected] = useState(null);
  const [subtab, setSubtab] = useState("active");
  const [summary, setSummary] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [editingKpi, setEditingKpi] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newWell, setNewWell] = useState({});

  async function loadWells() {
    const { data } = await api.get("/data/wells");
    const year = new Date().getFullYear();
    for (const w of data) {
      const { data: rs } = await api.get("/data/well_readings", { params: { well_id: w.id, year } });
      const reading = rs[0];
      const months = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];
      const ytd = reading ? months.reduce((a, m) => a + (reading[m] || 0), 0) : 0;
      w.ytd = ytd;
      w.pct = w.max_annual_m3 ? (ytd / w.max_annual_m3 * 100) : 0;
    }
    setWells(data);
  }

  async function loadSummary() {
    try {
      const { data } = await api.get("/wells/summary");
      setSummary(data);
    } catch (e) { console.error(e); }
  }

  async function loadOverrides() {
    try {
      const keys = ["wells_total_override", "wells_authorized_override", "wells_ytd_override", "wells_at_limit_override"];
      const m = {};
      for (const k of keys) {
        const { data } = await api.get("/data/tenant_config", { params: { key: k } });
        if (data.length) m[k] = data[0].value;
      }
      setOverrides(m);
    } catch (e) { console.error(e); }
  }

  async function load() {
    await Promise.all([loadWells(), loadSummary(), loadOverrides()]);
  }

  useEffect(() => { load(); }, []);

  async function saveOverride(key, value) {
    const n = value === "" || value == null ? null : Number(value);
    try {
      const { data: existing } = await api.get("/data/tenant_config", { params: { key } });
      if (existing.length) {
        if (n == null) {
          await api.delete(`/data/tenant_config/${existing[0].id}`);
        } else {
          await api.patch(`/data/tenant_config/${existing[0].id}`, { value: n });
        }
      } else if (n != null) {
        await api.post("/data/tenant_config", { key, value: n });
      }
      setOverrides((o) => ({ ...o, [key]: n }));
      await loadSummary();
    } catch (e) { console.error(e); alert("No se pudo guardar el valor."); }
    setEditingKpi(null);
  }

  // Computed values (override → summary → computed-from-wells)
  const computed = {
    total: wells.length,
    authorized: wells.reduce((a, w) => a + (w.max_annual_m3 || 0), 0),
    ytd: wells.reduce((a, w) => a + (w.ytd || 0), 0),
    atLimit: wells.filter((w) => w.pct >= 75).length,
  };
  const display = {
    total: overrides.wells_total_override ?? summary?.total_wells ?? computed.total,
    authorized: overrides.wells_authorized_override ?? summary?.total_authorized_m3 ?? computed.authorized,
    ytd: overrides.wells_ytd_override ?? summary?.total_ytd_m3 ?? computed.ytd,
    atLimit: overrides.wells_at_limit_override ?? summary?.at_limit ?? computed.atLimit,
  };
  const at90 = wells.filter((w) => w.pct >= 90).length;
  const limitVariant = display.atLimit > 0 ? (at90 > 0 ? "danger" : "warn") : "";

  return (
    <div>
      <div className="tabs-bar">
        <button className={`tab ${subtab === "active" ? "active" : ""}`} onClick={() => setSubtab("active")} data-testid="wells-subtab-active">Pozos activos</button>
        <button className={`tab ${subtab === "veda" ? "active" : ""}`} onClick={() => setSubtab("veda")} data-testid="wells-subtab-veda">Pozos en Veda</button>
      </div>
      {subtab === "veda" ? <WellsInVeda /> : (
      <>
      <div className="grid grid-cols-4 gap-3 mb-4">
        <EditableKpi
          label="Pozos totales"
          value={display.total}
          rawNumber
          editing={editingKpi === "total"}
          onEdit={() => setEditingKpi("total")}
          onSave={(v) => saveOverride("wells_total_override", v)}
          onCancel={() => setEditingKpi(null)}
          testId="wells-kpi-total"
        />
        <EditableKpi
          label="Vol. autorizado anual"
          value={`${Number(display.authorized).toLocaleString("es-MX")} M³`}
          numericValue={display.authorized}
          editing={editingKpi === "authorized"}
          onEdit={() => setEditingKpi("authorized")}
          onSave={(v) => saveOverride("wells_authorized_override", v)}
          onCancel={() => setEditingKpi(null)}
          testId="wells-kpi-authorized"
        />
        <EditableKpi
          label="Consumo YTD"
          value={`${Number(display.ytd).toFixed(0)} M³`}
          numericValue={display.ytd}
          editing={editingKpi === "ytd"}
          onEdit={() => setEditingKpi("ytd")}
          onSave={(v) => saveOverride("wells_ytd_override", v)}
          onCancel={() => setEditingKpi(null)}
          testId="wells-kpi-ytd"
        />
        <EditableKpi
          label="Pozos al límite"
          value={display.atLimit}
          rawNumber
          variant={limitVariant}
          editing={editingKpi === "atLimit"}
          onEdit={() => setEditingKpi("atLimit")}
          onSave={(v) => saveOverride("wells_at_limit_override", v)}
          onCancel={() => setEditingKpi(null)}
          testId="wells-kpi-atlimit"
        />
      </div>
      <div className="flex justify-end mb-3">
        <button className="btn btn-sm" onClick={load} data-testid="wells-recalc">↻ Recalcular YTD</button>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {wells.map((w) => {
          const badge = w.pct >= 90 ? "red" : w.pct >= 75 ? "yellow" : "green";
          return (
            <div key={w.id} className="card" style={{ cursor: "pointer", position: "relative" }} onClick={() => setSelected(w)} data-testid={`well-card-${w.id}`}>
              <button
                className="btn btn-sm btn-danger"
                onClick={async (e) => {
                  e.stopPropagation();
                  if (!window.confirm(`¿Eliminar ${w.name}? Esta acción eliminará todas sus lecturas históricas.`)) return;
                  if (!window.confirm("Confirmación final: esta acción NO se puede deshacer.")) return;
                  try {
                    // Delete readings then well
                    const { data: rs } = await api.get("/data/well_readings", { params: { well_id: w.id } });
                    for (const r of rs) {
                      try { await api.delete(`/data/well_readings/${r.id}`); } catch { /* ignore */ }
                    }
                    await api.delete(`/data/wells/${w.id}`);
                    load();
                  } catch (err) {
                    alert("No se pudo eliminar el pozo: " + (err.response?.data?.detail || err.message));
                  }
                }}
                style={{ position: "absolute", top: 8, right: 8, padding: "2px 6px", fontSize: 11 }}
                title="Eliminar pozo"
                data-testid={`well-${w.id}-delete`}
              >🗑️</button>
              <div className="flex justify-between items-start mb-3" style={{ paddingRight: 30 }}>
                <div>
                  <div className="font-semibold text-lg">{w.name}</div>
                  <div className="text-xs text-dim mt-2">{w.predio} · {w.authorization_number}</div>
                </div>
                <span className={`badge badge-${badge}`}>{w.pct >= 90 ? "CRÍTICO" : w.pct >= 75 ? "PRECAUCIÓN" : "NORMAL"}</span>
              </div>
              <div className="mt-3">
                <div className="text-2xl font-bold">{w.ytd.toFixed(0)} <span className="text-xs text-dim">M³</span></div>
                <div className="text-xs text-dim mt-2">de {w.max_annual_m3.toLocaleString("es-MX")} M³ autorizados</div>
                <div className="bar mt-2" style={{ background: "var(--bg-3)", height: 8, borderRadius: 4, overflow: "hidden" }}>
                  <div style={{ width: `${Math.min(100, w.pct)}%`, height: "100%", background: badge === "red" ? "var(--danger)" : badge === "yellow" ? "var(--warn)" : "var(--accent)" }} />
                </div>
                <div className="text-xs text-dim mt-2">{w.pct.toFixed(1)}% utilizado</div>
              </div>
            </div>
          );
        })}
        <div className="card" style={{ display: "grid", placeItems: "center", minHeight: 200 }}>
          <button className="btn btn-primary" onClick={() => { setNewWell({ name: "", predio: "", authorization_number: "", max_annual_m3: 50000, water_use: "", concession_start: "", concession_end: "", approved_readers: "", technical_responsible: "", location: "", status: "Activo" }); setAddOpen(true); }} data-testid="add-well">
            <Droplets size={16} /> Agregar Pozo
          </button>
        </div>
      </div>

      <WellsAnnualChart wells={wells} />

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Nuevo Pozo CONAGUA" size="lg" testId="new-well-modal">
        <div className="grid grid-cols-2 gap-3">
          <div><label className="label">Nombre/ID del pozo *</label><input className="input" value={newWell.name ?? ""} onChange={(e) => setNewWell({ ...newWell, name: e.target.value })} data-testid="new-well-name" /></div>
          <div><label className="label">Predio</label><input className="input" value={newWell.predio ?? ""} onChange={(e) => setNewWell({ ...newWell, predio: e.target.value })} /></div>
          <div><label className="label"># Autorización CONAGUA</label><input className="input" value={newWell.authorization_number ?? ""} onChange={(e) => setNewWell({ ...newWell, authorization_number: e.target.value })} /></div>
          <div><label className="label">Vol. máximo anual (M³)</label><input className="input" type="number" value={newWell.max_annual_m3 ?? 0} onChange={(e) => setNewWell({ ...newWell, max_annual_m3: Number(e.target.value) || 0 })} /></div>
          <div><label className="label">Uso del agua</label><input className="input" value={newWell.water_use ?? ""} onChange={(e) => setNewWell({ ...newWell, water_use: e.target.value })} /></div>
          <div><label className="label">Fecha inicio concesión</label><input className="input" type="date" value={newWell.concession_start ?? ""} onChange={(e) => setNewWell({ ...newWell, concession_start: e.target.value })} /></div>
          <div><label className="label">Fecha vencimiento</label><input className="input" type="date" value={newWell.concession_end ?? ""} onChange={(e) => setNewWell({ ...newWell, concession_end: e.target.value })} /></div>
          <div><label className="label">Tomadores de lectura aprobados</label><input className="input" value={newWell.approved_readers ?? ""} onChange={(e) => setNewWell({ ...newWell, approved_readers: e.target.value })} /></div>
          <div><label className="label">Responsable técnico</label><input className="input" value={newWell.technical_responsible ?? ""} onChange={(e) => setNewWell({ ...newWell, technical_responsible: e.target.value })} /></div>
          <div><label className="label">Ubicación</label><input className="input" value={newWell.location ?? ""} onChange={(e) => setNewWell({ ...newWell, location: e.target.value })} /></div>
          <div className="col-span-2 flex justify-end gap-2 mt-2">
            <button className="btn" onClick={() => setAddOpen(false)}>Cancelar</button>
            <button className="btn btn-primary" data-testid="new-well-save" onClick={async () => {
              if (!newWell.name) { alert("El nombre es obligatorio."); return; }
              try {
                await api.post("/data/wells", newWell);
                setAddOpen(false);
                load();
              } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); }
            }}>Crear pozo</button>
          </div>
        </div>
      </Modal>

      <Modal open={!!selected} onClose={() => setSelected(null)} title={selected?.name} size="xl">
        {selected && <WellDetail well={selected} onRefresh={load} />}
      </Modal>
      </>
      )}
    </div>
  );
}

function WellsAnnualChart({ wells }) {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [rowsByWell, setRowsByWell] = useState({}); // { well_id: reading row }
  const [allYears, setAllYears] = useState([currentYear, currentYear - 1, currentYear - 2]);
  const months = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
  const keys = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];

  async function load() {
    const byWell = {};
    const yearsSet = new Set([currentYear, currentYear - 1, currentYear - 2]);
    for (const w of wells) {
      const { data } = await api.get("/data/well_readings", { params: { well_id: w.id } });
      (data || []).forEach((r) => yearsSet.add(r.year));
      const row = (data || []).find((r) => r.year === year);
      if (row) byWell[w.id] = row;
    }
    setRowsByWell(byWell);
    setAllYears(Array.from(yearsSet).sort((a, b) => b - a));
  }

  useEffect(() => { if (wells.length) load(); /* eslint-disable-next-line */ }, [wells, year]);

  if (!wells.length) return null;

  // Build chart data: for each month, total consumption across wells + per-well series
  const palette = ["#1d9e75", "#3498db", "#e67e22", "#9b59b6", "#e74c3c", "#f1c40f", "#3aafa9", "#fd79a8"];
  let acc = 0;
  const totalAuthorized = wells.reduce((a, w) => a + (Number(w.max_annual_m3) || 0), 0);
  const monthlyAuthorized = totalAuthorized / 12;
  const chartData = months.map((m, i) => {
    const row = { month: m };
    let monthTotal = 0;
    wells.forEach((w) => {
      const v = Number(rowsByWell[w.id]?.[keys[i]]) || 0;
      row[w.name || w.id] = v;
      monthTotal += v;
    });
    row.__total = monthTotal;
    acc += monthTotal;
    row.__acumulado = acc;
    row.__limite = monthlyAuthorized;
    row.__tope = totalAuthorized;
    return row;
  });
  const ytdTotal = chartData[11].__acumulado;
  const pctTotal = totalAuthorized > 0 ? (ytdTotal / totalAuthorized * 100) : 0;

  return (
    <div className="card mt-4" data-testid="wells-annual-chart">
      <div className="card-title flex items-center justify-between flex-wrap gap-2">
        <span>Consumo anual de Pozos — Ene a Dic</span>
        <div className="flex gap-2 items-center">
          <span className="text-xs text-dim">Año:</span>
          <select className="select" value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="wells-annual-year">
            {allYears.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-3">
        <KpiBox label={`Consumo total YTD ${year}`} value={`${ytdTotal.toLocaleString("es-MX", { maximumFractionDigits: 1 })} M³`} />
        <KpiBox label="Autorizado anual (todos los pozos)" value={`${totalAuthorized.toLocaleString("es-MX")} M³`} />
        <KpiBox label="% utilizado total" value={`${pctTotal.toFixed(1)}%`} variant={pctTotal > 90 ? "danger" : pctTotal > 75 ? "warn" : ""} />
      </div>
      <ResponsiveContainer width="100%" height={340}>
        <ComposedChart data={chartData}>
          <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
          <XAxis dataKey="month" stroke="var(--text-dim)" fontSize={11} interval={0} />
          <YAxis yAxisId="left" stroke="var(--text-dim)" fontSize={11} label={{ value: "M³ por mes", angle: -90, position: "insideLeft", fill: "var(--text-dim)", fontSize: 10 }} />
          <YAxis yAxisId="right" orientation="right" stroke="var(--text-dim)" fontSize={11} label={{ value: "Acumulado M³", angle: 90, position: "insideRight", fill: "var(--text-dim)", fontSize: 10 }} />
          <Tooltip contentStyle={{ background: "var(--bg-2)", border: "1px solid var(--border-2)" }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {wells.map((w, i) => (
            <Bar key={w.id} yAxisId="left" dataKey={w.name || w.id} stackId="wells" fill={palette[i % palette.length]} />
          ))}
          <Line yAxisId="left" dataKey="__limite" stroke="#e74c3c" strokeDasharray="5 5" strokeWidth={2} dot={false} name="Límite mensual (autorizado/12)" />
          <Area yAxisId="right" dataKey="__acumulado" stroke="#3498db" fill="rgba(52,152,219,0.18)" name="Acumulado YTD (todos los pozos)" />
          <Line yAxisId="right" dataKey="__tope" stroke="#f1c40f" strokeDasharray="3 3" strokeWidth={1.5} dot={false} name="Tope anual autorizado (suma)" />
        </ComposedChart>
      </ResponsiveContainer>
      <div className="text-xs text-dim mt-2">Barras apiladas: consumo mensual por pozo. Línea roja: límite mensual (autorizado÷12). Área azul: acumulado YTD. Línea amarilla: tope anual autorizado.</div>
    </div>
  );
}

function EditableKpi({ label, value, numericValue, rawNumber, variant, editing, onEdit, onSave, onCancel, testId }) {
  const [draft, setDraft] = useState("");
  useEffect(() => {
    if (editing) {
      const seed = numericValue != null ? numericValue : (rawNumber ? value : "");
      setDraft(seed === "" ? "" : String(seed));
    }
  }, [editing, numericValue, rawNumber, value]);

  return (
    <div className={`kpi-card ${variant || ""}`} data-testid={testId} style={{ cursor: editing ? "default" : "pointer" }} onClick={() => !editing && onEdit && onEdit()}>
      <div className="label">{label}</div>
      {editing ? (
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <input
            type="number"
            step="any"
            className="input"
            value={draft}
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") onSave(draft); if (e.key === "Escape") onCancel(); }}
            style={{ width: 110, padding: 4 }}
            data-testid={`${testId}-input`}
          />
          <button className="btn btn-sm btn-primary" onClick={() => onSave(draft)} data-testid={`${testId}-save`}>✓ Guardar</button>
          <button className="btn btn-sm" onClick={onCancel}>✕</button>
        </div>
      ) : (
        <div className="value text-lg">{value}</div>
      )}
      {!editing && <div className="sub" style={{ fontSize: 10, color: "var(--text-dim)", marginTop: 4 }}>Click para editar</div>}
    </div>
  );
}

function WellsInVeda() {
  return (
    <div data-testid="wells-veda">
      <CrudTable
        collection="wells_veda"
        testIdPrefix="wells-veda"
        fields={[
          { key: "well_number", label: "Número de pozo", required: true },
          { key: "authorization_number", label: "# Autorización" },
          { key: "location", label: "Ubicación" },
          { key: "consumed_before_veda_m3", label: "Consumo previo a veda (M³)", type: "number" },
          { key: "veda_start_date", label: "Fecha puesta en veda", type: "date" },
          { key: "veda_release_estimate", label: "Liberación estimada", type: "date" },
          { key: "physical_tagging", label: "Etiquetado físico", type: "select", options: ["Sí", "No", "En proceso"] },
          { key: "comm_to_personnel", label: "Comunicado a personal", type: "select", options: ["Sí", "No", "En proceso", "Completado"] },
          { key: "observations", label: "Observaciones", type: "textarea", full: true },
        ]}
      />
    </div>
  );
}

function WellDetail({ well, onRefresh }) {
  const [tab, setTab] = useState("info");
  return (
    <div>
      <div className="tabs-bar">
        <button className={`tab ${tab === "info" ? "active" : ""}`} onClick={() => setTab("info")} data-testid={`well-${well.id}-tab-info`}>Ficha del Pozo</button>
        <button className={`tab ${tab === "readings" ? "active" : ""}`} onClick={() => setTab("readings")} data-testid={`well-${well.id}-tab-readings`}>Lecturas Mensuales</button>
        <button className={`tab ${tab === "payments" ? "active" : ""}`} onClick={() => setTab("payments")} data-testid={`well-${well.id}-tab-payments`}>Pagos y Derechos</button>
        <button className={`tab ${tab === "maint" ? "active" : ""}`} onClick={() => setTab("maint")} data-testid={`well-${well.id}-tab-maint`}>Mantenimientos</button>
        <button className={`tab ${tab === "auth" ? "active" : ""}`} onClick={() => setTab("auth")} data-testid={`well-${well.id}-tab-auth`}>Autorizaciones</button>
        <button className={`tab ${tab === "docs" ? "active" : ""}`} onClick={() => setTab("docs")} data-testid={`well-${well.id}-tab-docs`}>Documentos / Evidencias</button>
      </div>
      {tab === "info" && <WellInfoForm well={well} onSaved={onRefresh} />}
      {tab === "readings" && <Readings well={well} onParentRefresh={onRefresh} />}
      {tab === "payments" && <CrudTable collection="well_payments" filters={{ well_id: well.id }} parent={{ id: well.id, field: "well_id" }} testIdPrefix={`pay-${well.id}`} fields={[
        { key: "period", label: "Período", required: true },
        { key: "due_date", label: "Fecha límite", type: "date" },
        { key: "paid_at", label: "Fecha de pago", type: "date" },
        { key: "amount", label: "Monto ($MXP)", type: "number" },
        { key: "status", label: "Status", type: "select", options: ["Pagado", "Pendiente", "Por vencer", "Vencido"] },
        { key: "reference", label: "Referencia/folio" },
      ]} />}
      {tab === "maint" && <CrudTable collection="well_maintenances" filters={{ well_id: well.id }} parent={{ id: well.id, field: "well_id" }} testIdPrefix={`mt-${well.id}`} fields={[
        { key: "type", label: "Tipo", type: "select", options: ["Inspección", "Servicio"], required: true },
        { key: "description", label: "Descripción", type: "textarea", full: true },
        { key: "date", label: "Fecha", type: "date" },
        { key: "technician", label: "Técnico" },
        { key: "external_company", label: "Empresa externa" },
        { key: "cost", label: "Costo $", type: "number" },
      ]} />}
      {tab === "auth" && <CrudTable collection="well_authorizations" filters={{ well_id: well.id }} parent={{ id: well.id, field: "well_id" }} testIdPrefix={`auth-${well.id}`} fields={[
        { key: "doc_type", label: "Tipo de documento", required: true },
        { key: "number", label: "Número" },
        { key: "issued_at", label: "Emisión", type: "date" },
        { key: "expires_at", label: "Vencimiento", type: "date" },
        { key: "status", label: "Status", type: "select", options: ["Vigente", "Por vencer", "Vencido"] },
      ]} />}
      {tab === "docs" && <FileCCE module="wells" parentId={well.id} />}
    </div>
  );
}

function WellInfoForm({ well, onSaved }) {
  const [form, setForm] = useState({ ...well });
  const [status, setStatus] = useState(""); // "saving"|"ok"|"error"
  const fields = [
    { k: "name", label: "Nombre / ID del pozo", required: true },
    { k: "predio", label: "Predio" },
    { k: "authorization_number", label: "# Autorización CONAGUA" },
    { k: "max_annual_m3", label: "Volumen máximo anual autorizado (M³)", type: "number" },
    { k: "water_use", label: "Uso del agua" },
    { k: "concession_start", label: "Fecha inicio concesión", type: "date" },
    { k: "concession_end", label: "Fecha vencimiento concesión", type: "date" },
    { k: "approved_readers", label: "Tomadores de lectura aprobados", type: "textarea" },
    { k: "technical_responsible", label: "Responsable técnico" },
    { k: "location", label: "Ubicación", type: "textarea" },
    { k: "latitude", label: "Latitud" },
    { k: "longitude", label: "Longitud" },
    { k: "depth_m", label: "Profundidad (m)", type: "number" },
    { k: "flow_rate_lps", label: "Caudal autorizado (L/s)", type: "number" },
    { k: "aquifer", label: "Acuífero / Zona hidrológica" },
    { k: "status", label: "Status operativo", type: "select", options: ["Activo", "Suspendido", "En veda", "Inactivo"] },
    { k: "observations", label: "Observaciones", type: "textarea", full: true },
  ];

  async function save() {
    setStatus("saving");
    const payload = { ...form };
    delete payload.id; delete payload.tenant_id; delete payload.created_at; delete payload.updated_at; delete payload._id;
    try {
      await api.patch(`/data/wells/${well.id}`, payload);
      setStatus("ok");
      onSaved && onSaved();
      setTimeout(() => setStatus(""), 1800);
    } catch (e) {
      setStatus("error");
      alert("Error al guardar la ficha: " + (e.response?.data?.detail || e.message));
    }
  }

  return (
    <div>
      <div className="flex justify-end mb-3">
        <button
          className="btn btn-primary btn-sm"
          onClick={save}
          disabled={status === "saving"}
          data-testid={`well-${well.id}-info-save`}
          style={{ background: status === "ok" ? "var(--success)" : undefined }}
        >
          {status === "saving" ? <span className="spinner" /> : status === "ok" ? "✓ Guardado" : "💾 Guardar ficha"}
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {fields.map((f) => (
          <div key={f.k} style={f.full ? { gridColumn: "1 / -1" } : undefined}>
            <label className="label">{f.label}{f.required && " *"}</label>
            {f.type === "select" ? (
              <select className="select" value={form[f.k] ?? ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} data-testid={`well-info-${f.k}`}>
                <option value="">--</option>
                {(f.options || []).map((o) => <option key={o}>{o}</option>)}
              </select>
            ) : f.type === "textarea" ? (
              <textarea className="textarea" rows={2} value={form[f.k] ?? ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} data-testid={`well-info-${f.k}`} />
            ) : (
              <input className="input" type={f.type || "text"} step="any" value={form[f.k] ?? ""} onChange={(e) => setForm({ ...form, [f.k]: f.type === "number" ? (Number(e.target.value) || 0) : e.target.value })} data-testid={`well-info-${f.k}`} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Readings({ well, onParentRefresh }) {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [reading, setReading] = useState(null);
  const [tick, setTick] = useState(0);
  const [saveStatus, setSaveStatus] = useState({}); // { jan: "saving" | "ok" | "error" }
  const [saveAllStatus, setSaveAllStatus] = useState(""); // "saving"|"ok"|"error"
  const [history, setHistory] = useState([]); // all years for this well
  const months = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
  const keys = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];

  async function load() {
    const { data } = await api.get("/data/well_readings", { params: { well_id: well.id } });
    setHistory(data || []);
    const row = (data || []).find((r) => r.year === year);
    if (row) setReading(row);
    else {
      const empty = { well_id: well.id, year };
      keys.forEach((k) => empty[k] = 0);
      const { data: created } = await api.post("/data/well_readings", empty);
      setReading(created);
      setHistory((h) => [...h, created]);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [well.id, tick, year]);

  function applyLocal(key, val) {
    const n = val === "" ? 0 : Number(val);
    setReading((r) => ({ ...r, [key]: Number.isNaN(n) ? 0 : n }));
  }

  async function persist(key, val) {
    applyLocal(key, val);
    const n = val === "" ? 0 : Number(val);
    if (!reading?.id) {
      const empty = { well_id: well.id, year };
      keys.forEach((k) => empty[k] = (k === key) ? n : (reading?.[k] || 0));
      try {
        const { data: created } = await api.post("/data/well_readings", empty);
        setReading(created);
        setHistory((h) => [...h.filter((x) => x.id !== created.id), created]);
        setSaveStatus((s) => ({ ...s, [key]: "ok" }));
        onParentRefresh && onParentRefresh();
      } catch (e) {
        setSaveStatus((s) => ({ ...s, [key]: "error" }));
        console.error("Error creando lectura:", e);
      }
      return;
    }
    setSaveStatus((s) => ({ ...s, [key]: "saving" }));
    try {
      await api.patch(`/wells/${well.id}/readings?year=${year}`, { [key]: n });
      setSaveStatus((s) => ({ ...s, [key]: "ok" }));
      onParentRefresh && onParentRefresh();
      setTimeout(() => setSaveStatus((s) => ({ ...s, [key]: null })), 1500);
    } catch (e) {
      setSaveStatus((s) => ({ ...s, [key]: "error" }));
      console.error("Error guardando lectura:", e);
    }
  }

  async function saveAll() {
    if (!reading) return;
    setSaveAllStatus("saving");
    try {
      const payload = {};
      keys.forEach((k) => { payload[k] = Number(reading[k]) || 0; });
      await api.patch(`/wells/${well.id}/readings?year=${year}`, payload);
      setSaveAllStatus("ok");
      onParentRefresh && onParentRefresh();
      setTimeout(() => setSaveAllStatus(""), 1800);
    } catch (e) {
      setSaveAllStatus("error");
      console.error("Error guardando:", e);
      alert("No se pudo guardar. Intenta de nuevo.");
    }
  }

  if (!reading) return <div className="text-dim">Cargando...</div>;
  const ytd = keys.reduce((a, k) => a + (Number(reading[k]) || 0), 0);
  const limit_per_month = (well.max_annual_m3 || 0) / 12;
  // Build chart data with consumption, monthly limit and cumulative ytd
  let acc = 0;
  const chartData = months.map((m, i) => {
    const v = Number(reading[keys[i]]) || 0;
    acc += v;
    return { month: m, consumo: v, limite: limit_per_month, acumulado: acc, autorizado: well.max_annual_m3 || 0 };
  });
  const pct = (ytd / (well.max_annual_m3 || 1)) * 100;
  const remaining = (well.max_annual_m3 || 0) - ytd;
  const availableYears = Array.from(new Set([currentYear, currentYear - 1, currentYear - 2, ...history.map((h) => h.year)])).sort((a, b) => b - a);

  return (
    <div>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="text-xs text-dim">Año:</span>
          <select className="select" value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid={`well-${well.id}-year`}>
            {availableYears.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <span className="text-xs text-dim ml-3">Click directo en cualquier celda. Enter o blur para guardar.</span>
        </div>
        <div className="flex gap-2 items-center">
          <button className="btn btn-sm" onClick={() => setTick(tick + 1)} data-testid={`well-${well.id}-recalc`}>↻ Recalcular YTD</button>
          <button
            className="btn btn-sm btn-primary"
            onClick={saveAll}
            disabled={saveAllStatus === "saving"}
            data-testid={`well-${well.id}-save-all`}
            style={{ background: saveAllStatus === "ok" ? "var(--success)" : undefined }}
          >
            {saveAllStatus === "saving" ? <span className="spinner" /> : saveAllStatus === "ok" ? "✓ Guardado" : "💾 Guardar año"}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-3 mb-3">
        <KpiBox label={`YTD ${year}`} value={`${ytd.toFixed(1)} M³`} />
        <KpiBox label="Autorizado anual" value={`${(well.max_annual_m3 || 0).toLocaleString("es-MX")} M³`} />
        <KpiBox label="% utilizado" value={`${pct.toFixed(1)}%`} variant={pct > 90 ? "danger" : pct > 75 ? "warn" : ""} />
        <KpiBox label="Restante anual" value={`${remaining.toFixed(0)} M³`} variant={remaining < 0 ? "danger" : ""} />
      </div>
      <div className="table-wrap mb-4" style={{ overflowX: "auto", maxWidth: "100%" }}>
        <table className="table" style={{ minWidth: 1300 }}>
          <thead><tr><th>Concepto</th>{months.map((m) => <th key={m}>{m}</th>)}<th>YTD</th></tr></thead>
          <tbody>
            <tr>
              <td><b>Consumo {year} (M³)</b></td>
              {keys.map((k) => {
                const st = saveStatus[k];
                return (
                  <td key={k} style={{ position: "relative" }}>
                    <input
                      className="input"
                      style={{ width: 80, padding: 4, paddingRight: 18,
                               borderColor: st === "error" ? "var(--danger)" : st === "ok" ? "var(--accent)" : undefined }}
                      type="number"
                      step="any"
                      value={reading[k] ?? 0}
                      onChange={(e) => applyLocal(k, e.target.value)}
                      onBlur={(e) => persist(k, e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                      data-testid={`reading-${k}`}
                    />
                    {st && (
                      <span style={{ position: "absolute", right: 4, top: "50%", transform: "translateY(-50%)", fontSize: 11 }}>
                        {st === "saving" ? "⟳" : st === "ok" ? <span style={{ color: "var(--accent)" }}>✓</span> : <span style={{ color: "var(--danger)" }}>✗</span>}
                      </span>
                    )}
                  </td>
                );
              })}
              <td><b>{ytd.toFixed(1)}</b></td>
            </tr>
            <tr style={{ background: "rgba(255,255,255,0.02)" }}>
              <td><b>Acumulado (M³)</b></td>
              {chartData.map((d, i) => (
                <td key={i} style={{ fontSize: 12, color: d.acumulado > (well.max_annual_m3 || 0) ? "var(--danger)" : "var(--text-dim)" }}>
                  {d.acumulado.toFixed(0)}
                </td>
              ))}
              <td><b>{ytd.toFixed(1)}</b></td>
            </tr>
            <tr>
              <td><b>% del anual</b></td>
              {chartData.map((d, i) => {
                const p = (well.max_annual_m3 ? (d.acumulado / well.max_annual_m3 * 100) : 0);
                return (
                  <td key={i} style={{ fontSize: 12, color: p > 90 ? "var(--danger)" : p > 75 ? "var(--warn)" : "var(--accent)" }}>
                    {p.toFixed(0)}%
                  </td>
                );
              })}
              <td><b>{pct.toFixed(1)}%</b></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="card-title text-sm">Consumo mensual {year} — {well.name}</div>
      <ResponsiveContainer width="100%" height={320}>
        <ComposedChart data={chartData}>
          <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
          <XAxis dataKey="month" stroke="var(--text-dim)" fontSize={11} interval={0} />
          <YAxis yAxisId="left" stroke="var(--text-dim)" fontSize={11} label={{ value: "M³ por mes", angle: -90, position: "insideLeft", fill: "var(--text-dim)", fontSize: 10 }} />
          <YAxis yAxisId="right" orientation="right" stroke="var(--text-dim)" fontSize={11} label={{ value: "Acumulado M³", angle: 90, position: "insideRight", fill: "var(--text-dim)", fontSize: 10 }} />
          <Tooltip contentStyle={{ background: "var(--bg-2)", border: "1px solid var(--border-2)" }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar yAxisId="left" dataKey="consumo" fill="#1d9e75" name="Consumo (M³)" />
          <Line yAxisId="left" dataKey="limite" stroke="#e74c3c" strokeDasharray="5 5" strokeWidth={2} dot={false} name="Límite mensual (autorizado/12)" />
          <Area yAxisId="right" dataKey="acumulado" stroke="#3498db" fill="rgba(52,152,219,0.18)" name="Acumulado YTD" />
          <Line yAxisId="right" dataKey="autorizado" stroke="#f1c40f" strokeDasharray="3 3" strokeWidth={1.5} dot={false} name="Tope anual autorizado" />
        </ComposedChart>
      </ResponsiveContainer>

      {/* Multi-year comparative if there is more than 1 year of history */}
      {history.length > 1 && (
        <>
          <div className="card-title text-sm mt-4">Comparativo plurianual</div>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart>
              <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
              <XAxis dataKey="month" type="category" allowDuplicatedCategory={false} stroke="var(--text-dim)" fontSize={11} interval={0} />
              <YAxis stroke="var(--text-dim)" fontSize={11} />
              <Tooltip contentStyle={{ background: "var(--bg-2)", border: "1px solid var(--border-2)" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {history.sort((a, b) => a.year - b.year).map((r, idx) => {
                const data = months.map((m, i) => ({ month: m, value: Number(r[keys[i]]) || 0 }));
                const palette = ["#1d9e75", "#3498db", "#e67e22", "#9b59b6", "#e74c3c", "#f1c40f"];
                return <Line key={r.id} dataKey="value" data={data} name={`${r.year}`} stroke={palette[idx % palette.length]} strokeWidth={2} dot={false} />;
              })}
            </LineChart>
          </ResponsiveContainer>
        </>
      )}
    </div>
  );
}

function PTAR() {
  const [readings, setReadings] = useState([]);
  const [saveStatus, setSaveStatus] = useState({}); // keyed by "month-field"
  const [saveAllStatus, setSaveAllStatus] = useState("");
  const [pricePerM3, setPricePerM3] = useState(0);
  const [priceLoaded, setPriceLoaded] = useState(false);
  const year = new Date().getFullYear();
  const keys = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];
  const months = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];

  async function load() {
    const { data } = await api.get("/data/ptar_readings", { params: { year } });
    setReadings(data);
  }
  async function loadPrice() {
    try {
      const { data } = await api.get("/data/tenant_config", { params: { key: "ptar_price_per_m3" } });
      if (data.length) setPricePerM3(Number(data[0].value) || 0);
    } catch { /* ignore */ }
    setPriceLoaded(true);
  }
  useEffect(() => { load(); loadPrice(); /* eslint-disable-next-line */ }, []);

  const reading = readings[0];
  const total = (field) => reading ? keys.reduce((a, k) => a + (reading[k]?.[field] || 0), 0) : 0;

  async function ensureReading() {
    if (reading?.id) return reading;
    const empty = { year };
    keys.forEach((k) => empty[k] = (reading && reading[k]) ? reading[k] : { treated: 0, discharged: 0, reused: 0, cost: 0 });
    const { data: created } = await api.post("/data/ptar_readings", empty);
    setReadings([created]);
    return created;
  }

  function applyLocal(monthKey, field, val) {
    setReadings((prev) => {
      const r = prev[0] || { year };
      const updated = { ...(r[monthKey] || { treated: 0, discharged: 0, reused: 0, cost: 0 }) };
      updated[field] = Number(val) || 0;
      // Auto-recompute cost if user edits discharged and didn't manually override cost yet
      if (field === "discharged" && !updated.cost_manual) {
        updated.cost = (Number(val) || 0) * (pricePerM3 || 0);
      }
      if (field === "cost") {
        updated.cost_manual = true;
      }
      return [{ ...r, [monthKey]: updated }];
    });
  }

  async function persist(monthKey, field, val) {
    applyLocal(monthKey, field, val);
    setSaveStatus((s) => ({ ...s, [`${monthKey}-${field}`]: "saving" }));
    try {
      const r = await ensureReading();
      const existing = r[monthKey] || { treated: 0, discharged: 0, reused: 0, cost: 0 };
      const updated = { ...existing, [field]: Number(val) || 0 };
      if (field === "discharged" && !updated.cost_manual) updated.cost = (Number(val) || 0) * (pricePerM3 || 0);
      if (field === "cost") updated.cost_manual = true;
      await api.patch(`/data/ptar_readings/${r.id}`, { [monthKey]: updated });
      setSaveStatus((s) => ({ ...s, [`${monthKey}-${field}`]: "ok" }));
      setTimeout(() => setSaveStatus((s) => ({ ...s, [`${monthKey}-${field}`]: null })), 1500);
    } catch (e) {
      console.error("Error guardando PTAR:", e);
      setSaveStatus((s) => ({ ...s, [`${monthKey}-${field}`]: "error" }));
    }
  }

  async function saveAll() {
    setSaveAllStatus("saving");
    try {
      const r = await ensureReading();
      const payload = {};
      keys.forEach((k) => { payload[k] = r[k] || { treated: 0, discharged: 0, reused: 0, cost: 0 }; });
      await api.patch(`/data/ptar_readings/${r.id}`, payload);
      setSaveAllStatus("ok");
      setTimeout(() => setSaveAllStatus(""), 1800);
    } catch (e) {
      setSaveAllStatus("error");
      alert("No se pudo guardar. Intenta de nuevo.");
    }
  }

  async function savePrice(v) {
    const n = Number(v) || 0;
    setPricePerM3(n);
    try {
      const { data: existing } = await api.get("/data/tenant_config", { params: { key: "ptar_price_per_m3" } });
      if (existing.length) await api.patch(`/data/tenant_config/${existing[0].id}`, { value: n });
      else await api.post("/data/tenant_config", { key: "ptar_price_per_m3", value: n });
      // Re-compute cost for every month based on the new price unless manually overridden
      if (reading?.id) {
        const updates = {};
        keys.forEach((k) => {
          const m = reading[k] || { treated: 0, discharged: 0, reused: 0, cost: 0 };
          if (!m.cost_manual) {
            updates[k] = { ...m, cost: (Number(m.discharged) || 0) * n };
          }
        });
        if (Object.keys(updates).length) {
          setReadings([{ ...reading, ...updates }]);
          try { await api.patch(`/data/ptar_readings/${reading.id}`, updates); } catch { /* ignore */ }
        }
      }
    } catch (e) { console.error(e); }
  }

  const totalCost = total("cost");

  return (
    <div>
      <div className="card mb-3" style={{ background: "rgba(29,158,117,0.06)" }}>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="label" style={{ margin: 0 }}>Precio por M³ ($MXP):</label>
          <input
            type="number"
            step="any"
            className="input"
            style={{ width: 140 }}
            value={priceLoaded ? pricePerM3 : ""}
            onChange={(e) => setPricePerM3(Number(e.target.value) || 0)}
            onBlur={(e) => savePrice(e.target.value)}
            data-testid="ptar-price-per-m3"
          />
          <span className="text-xs text-dim">El costo de cada mes se calcula como Volumen descargado × Precio. Editar el costo manualmente lo sobrescribe.</span>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3 mb-4">
        <KpiBox label="Tratado YTD" value={`${total("treated").toFixed(1)} M³`} />
        <KpiBox label="Descargado YTD" value={`${total("discharged").toFixed(1)} M³`} />
        <KpiBox label="Reutilizado YTD" value={`${total("reused").toFixed(1)} M³`} />
        <KpiBox label="Costo YTD" value={`$${totalCost.toLocaleString("es-MX", { maximumFractionDigits: 2 })}`} />
      </div>
      <div className="card">
        <div className="card-title flex items-center justify-between">
          <span>Lecturas mensuales {year}</span>
          <div className="flex gap-2">
            <button className="btn btn-sm" onClick={load} title="Recargar">↻ Recalcular YTD</button>
            <button
              className="btn btn-sm btn-primary"
              onClick={saveAll}
              data-testid="ptar-save-all"
              disabled={saveAllStatus === "saving"}
              style={{ background: saveAllStatus === "ok" ? "var(--success)" : undefined }}
            >
              {saveAllStatus === "saving" ? <span className="spinner" /> : saveAllStatus === "ok" ? "✓ Guardado" : "💾 Guardar"}
            </button>
          </div>
        </div>
        <div className="table-wrap" style={{ overflowX: "auto", maxWidth: "100%" }}>
          <table className="table" style={{ minWidth: 1300 }}>
            <thead><tr><th>Concepto</th>{months.map((m) => <th key={m}>{m}</th>)}<th>YTD</th></tr></thead>
            <tbody>
              {["treated", "discharged", "reused", "cost"].map((f) => (
                <tr key={f}>
                  <td><b>{f === "treated" ? "Tratado" : f === "discharged" ? "Descargado" : f === "reused" ? "Reutilizado" : "Costo ($MXP)"}</b></td>
                  {keys.map((k) => {
                    const st = saveStatus[`${k}-${f}`];
                    return (
                      <td key={k} style={{ position: "relative" }}>
                        <input
                          className="input"
                          style={{ width: 80, padding: 4, paddingRight: 18,
                                   borderColor: st === "error" ? "var(--danger)" : st === "ok" ? "var(--accent)" : undefined }}
                          type="number"
                          step="any"
                          value={reading?.[k]?.[f] ?? 0}
                          onChange={(e) => applyLocal(k, f, e.target.value)}
                          onBlur={(e) => persist(k, f, e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                          data-testid={`ptar-${f}-${k}`}
                        />
                        {st && (
                          <span style={{ position: "absolute", right: 4, top: "50%", transform: "translateY(-50%)", fontSize: 11 }}>
                            {st === "saving" ? "⟳" : st === "ok" ? <span style={{ color: "var(--accent)" }}>✓</span> : <span style={{ color: "var(--danger)" }}>✗</span>}
                          </span>
                        )}
                      </td>
                    );
                  })}
                  <td><b>{total(f).toFixed(f === "cost" ? 0 : 1)}</b></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Chart with dual Y axis: M³ (left) + cost (right) */}
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={months.map((m, i) => ({
            month: m,
            descargado: Number(reading?.[keys[i]]?.discharged) || 0,
            tratado: Number(reading?.[keys[i]]?.treated) || 0,
            reutilizado: Number(reading?.[keys[i]]?.reused) || 0,
            costo: Number(reading?.[keys[i]]?.cost) || 0,
          }))}>
            <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
            <XAxis dataKey="month" stroke="var(--text-dim)" fontSize={11} interval={0} />
            <YAxis yAxisId="left" stroke="var(--text-dim)" fontSize={11} label={{ value: "M³", position: "insideLeft", angle: -90, dy: 10, fontSize: 10 }} />
            <YAxis yAxisId="right" orientation="right" stroke="var(--text-dim)" fontSize={11} label={{ value: "$MXP", position: "insideRight", angle: 90, dy: -10, fontSize: 10 }} />
            <Tooltip contentStyle={{ background: "var(--bg-2)", border: "1px solid var(--border-2)" }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar yAxisId="left" dataKey="descargado" fill="#3498db" name="Descargado (M³)" />
            <Bar yAxisId="left" dataKey="tratado" fill="#1d9e75" name="Tratado (M³)" />
            <Bar yAxisId="left" dataKey="reutilizado" fill="#f1c40f" name="Reutilizado (M³)" />
            <Bar yAxisId="right" dataKey="costo" fill="#e74c3c" name="Costo ($MXP)" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function KpiBox({ label, value, variant }) {
  return <div className={`kpi-card ${variant || ""}`}><div className="label">{label}</div><div className="value text-lg">{value}</div></div>;
}

// ----- Energy -----
function EnergyTab() {
  return (
    <MonthlyReadings
      collection="energy_readings"
      title="Lecturas mensuales de energía (KWH)"
      numericKeys={[
        { key: "cfe", label: "CFE (KWH)" },
        { key: "solar", label: "Solar (KWH)" },
      ]}
      extraColumns={[
        { key: "cost_mxp", label: "Costo $MXP", type: "number" },
      ]}
      headerCards={(_row, ytd) => {
        const cfe = ytd.cfe || 0;
        const sol = ytd.solar || 0;
        const total = cfe + sol;
        const co2 = cfe * CFE_FACTOR;
        return [
          { label: "KWH CFE YTD", value: cfe.toLocaleString("es-MX") },
          { label: "KWH Solar YTD", value: sol.toLocaleString("es-MX") },
          { label: "Total KWH YTD", value: total.toLocaleString("es-MX") },
          { label: "TON CO₂eq YTD", value: co2.toFixed(2), sub: "CFE × 0.432 kg/KWH · Solar = 0" },
        ];
      }}
      chart={{
        title: "Consumo mensual: CFE vs Solar",
        series: [
          { key: "cfe", label: "CFE", color: "#1d9e75" },
          { key: "solar", label: "Solar", color: "#f1c40f" },
        ],
      }}
    />
  );
}

// ----- Fuels -----
function FuelsTab({ subFromNav }) {
  const [sub, setSub] = useState(subFromNav === "gasoline" ? "gasoline" : "diesel");
  useEffect(() => { if (subFromNav === "gasoline" || subFromNav === "diesel") setSub(subFromNav); }, [subFromNav]);
  return (
    <div>
      <div className="tabs-bar">
        {[["diesel","Diesel"],["gasoline","Gasolina"]].map(([k,l]) => (
          <button key={k} className={`tab ${sub===k?"active":""}`} onClick={() => setSub(k)} data-testid={`fuel-tab-${k}`}>{l}</button>
        ))}
      </div>
      <FuelSubTab fuel={sub} />
    </div>
  );
}

function FuelSubTab({ fuel }) {
  const factor = fuel === "diesel" ? DIESEL_FACTOR : GASOLINE_FACTOR;
  const factorLabel = fuel === "diesel" ? "2.65 kg/L" : "2.31 kg/L";
  const [price, setPrice] = useState(0);
  const [priceLoaded, setPriceLoaded] = useState(false);

  // Load tenant-level fuel price config
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/data/tenant_config`, { params: { key: `fuel_price_${fuel}` } });
        if (data.length) setPrice(Number(data[0].value) || 0);
      } catch { /* ignore */ }
      setPriceLoaded(true);
    })();
  }, [fuel]);

  async function savePrice(v) {
    const n = Number(v) || 0;
    setPrice(n);
    try {
      const { data: existing } = await api.get(`/data/tenant_config`, { params: { key: `fuel_price_${fuel}` } });
      if (existing.length) await api.patch(`/data/tenant_config/${existing[0].id}`, { value: n });
      else await api.post(`/data/tenant_config`, { key: `fuel_price_${fuel}`, value: n });
    } catch (e) { console.error(e); }
    // Ask the user whether to back-fill historical costs
    if (n > 0) {
      const choice = window.prompt(
        "¿Recalcular los costos históricos con el nuevo precio?\n" +
        "Escribe: 'todos' (todos los registros), 'nuevos' (solo nuevos), o 'cancelar'",
        "nuevos"
      );
      if (choice === "todos") {
        try {
          const { data: rows } = await api.get(`/data/fuel_readings`, { params: { fuel } });
          for (const r of rows) {
            const months = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];
            const totalVol = months.reduce((a, m) => a + (Number(r[`volume_l_${m}`]) || 0), 0);
            const cost = totalVol * n;
            await api.patch(`/data/fuel_readings/${r.id}`, { cost_mxp: cost, price_per_l: n });
          }
          alert(`Se recalcularon ${rows.length} registros.`);
        } catch (e) { alert("Error: " + e.message); }
      }
    }
  }

  return (
    <div>
      <div className="card mb-3" style={{ background: "rgba(29,158,117,0.06)" }}>
        <div className="flex items-center gap-3">
          <label className="label" style={{ margin: 0 }}>Precio por litro ($MXP):</label>
          <input
            type="number"
            step="any"
            className="input"
            style={{ width: 140 }}
            value={priceLoaded ? price : ""}
            onChange={(e) => setPrice(Number(e.target.value) || 0)}
            onBlur={(e) => savePrice(e.target.value)}
            data-testid={`fuel-price-${fuel}`}
          />
          <span className="text-xs text-dim">El costo de cada mes se calcula como Volumen × Precio. Editar el costo manualmente lo sobrescribe.</span>
        </div>
      </div>
      <MonthlyReadings
        key={fuel}
        collection="fuel_readings"
        title={`Consumo mensual de ${fuel === "diesel" ? "Diesel" : "Gasolina"} (L)`}
        filters={{ fuel }}
        numericKeys={[
          { key: "volume_l", label: "Volumen (L)" },
        ]}
        extraColumns={[
          { key: "equipment", label: "Equipo/Vehículo", type: "text" },
          { key: "area", label: "Área/Dpto", type: "text" },
          { key: "cost_mxp", label: "Costo $MXP (auto / editable)", type: "number" },
        ]}
        headerCards={(row, ytd) => {
          const v = ytd.volume_l || 0;
          const cost = (row?.cost_mxp ?? 0) || (v * price);
          return [
            { label: "Volumen YTD (L)", value: v.toLocaleString("es-MX") },
            { label: "Costo YTD ($MXP)", value: `$${cost.toLocaleString("es-MX", { maximumFractionDigits: 2 })}`, sub: price > 0 ? `Auto: ${v} × $${price}` : "Sin precio configurado" },
            { label: "CO₂eq YTD (TON)", value: (v * factor).toFixed(2), sub: factorLabel },
          ];
        }}
        chart={{
          title: `${fuel === "diesel" ? "Diesel" : "Gasolina"} mensual`,
          series: [{ key: "volume_l", label: "Litros", color: fuel === "diesel" ? "#e67e22" : "#9b59b6" }],
        }}
      />

      <FuelAreas fuel={fuel} price={price} />
    </div>
  );
}

// ----- BLOQUE 3 — Consumo por Área/Patio -----
function FuelAreas({ fuel, price }) {
  const year = new Date().getFullYear();
  const monthKeys = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];
  const monthLbl = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
  const [rows, setRows] = useState([]);
  const [saveStatus, setSaveStatus] = useState({}); // "rowId-key": status
  const [saveAllStatus, setSaveAllStatus] = useState("");

  async function load() {
    try {
      const { data } = await api.get("/data/fuel_area_readings", { params: { fuel, year } });
      setRows(data);
    } catch (e) { console.error(e); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fuel, year]);

  async function addArea() {
    const name = window.prompt("Nombre del área/patio (ej. Patio Neptuno):");
    if (!name) return;
    const empty = { fuel, year, area_name: name.trim() };
    monthKeys.forEach((k) => empty[k] = 0);
    try {
      const { data } = await api.post("/data/fuel_area_readings", empty);
      setRows((r) => [...r, data]);
    } catch (e) { alert("Error: " + e.message); }
  }

  async function delArea(row) {
    if (!window.confirm(`¿Eliminar área "${row.area_name}"?`)) return;
    try {
      await api.delete(`/data/fuel_area_readings/${row.id}`);
      setRows((r) => r.filter((x) => x.id !== row.id));
    } catch (e) { alert("Error: " + e.message); }
  }

  function applyCell(rowId, key, val) {
    setRows((rs) => rs.map((r) => r.id === rowId ? { ...r, [key]: Number(val) || 0 } : r));
  }

  async function persistCell(row, key, val) {
    const n = Number(val) || 0;
    applyCell(row.id, key, n);
    setSaveStatus((s) => ({ ...s, [`${row.id}-${key}`]: "saving" }));
    try {
      await api.patch(`/data/fuel_area_readings/${row.id}`, { [key]: n });
      setSaveStatus((s) => ({ ...s, [`${row.id}-${key}`]: "ok" }));
      setTimeout(() => setSaveStatus((s) => ({ ...s, [`${row.id}-${key}`]: null })), 1500);
    } catch (e) {
      setSaveStatus((s) => ({ ...s, [`${row.id}-${key}`]: "error" }));
    }
  }

  async function saveAreaName(row, name) {
    setRows((rs) => rs.map((r) => r.id === row.id ? { ...r, area_name: name } : r));
    try { await api.patch(`/data/fuel_area_readings/${row.id}`, { area_name: name }); } catch (e) { console.error(e); }
  }

  async function saveAll() {
    setSaveAllStatus("saving");
    try {
      for (const r of rows) {
        const payload = { area_name: r.area_name };
        monthKeys.forEach((k) => { payload[k] = Number(r[k]) || 0; });
        await api.patch(`/data/fuel_area_readings/${r.id}`, payload);
      }
      setSaveAllStatus("ok");
      setTimeout(() => setSaveAllStatus(""), 1800);
    } catch (e) {
      setSaveAllStatus("error");
      alert("Error al guardar áreas: " + e.message);
    }
  }

  const palette = ["#1d9e75", "#3498db", "#e67e22", "#9b59b6", "#e74c3c", "#f1c40f", "#3aafa9"];
  const chartData = monthLbl.map((m, i) => {
    const row = { month: m };
    rows.forEach((r) => { row[r.area_name || r.id] = Number(r[monthKeys[i]]) || 0; });
    return row;
  });

  return (
    <div className="card mt-4" data-testid={`fuel-areas-${fuel}`}>
      <div className="card-title flex items-center justify-between flex-wrap gap-2">
        <span>Consumo por Área / Patio — {fuel === "diesel" ? "Diesel" : "Gasolina"} {year}</span>
        <div className="flex gap-2">
          <button className="btn btn-sm btn-primary" onClick={addArea} data-testid={`fuel-area-add-${fuel}`}>+ Agregar área/patio</button>
          <button
            className="btn btn-sm btn-primary"
            onClick={saveAll}
            disabled={saveAllStatus === "saving" || rows.length === 0}
            data-testid={`fuel-area-save-all-${fuel}`}
            style={{ background: saveAllStatus === "ok" ? "var(--success)" : undefined }}
          >
            {saveAllStatus === "saving" ? <span className="spinner" /> : saveAllStatus === "ok" ? "✓ Guardado" : "💾 Guardar"}
          </button>
        </div>
      </div>
      <div className="table-wrap" style={{ overflowX: "auto", maxWidth: "100%" }}>
        <table className="table" style={{ minWidth: 1200 }}>
          <thead>
            <tr>
              <th>Área / Patio</th>
              {monthLbl.map((m) => <th key={m}>{m}</th>)}
              <th>YTD (L)</th>
              <th>Costo YTD ($)</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={monthLbl.length + 4} className="text-dim text-center">Sin áreas registradas. Usa el botón "+ Agregar área/patio" para empezar.</td></tr>
            ) : rows.map((r) => {
              const ytd = monthKeys.reduce((a, k) => a + (Number(r[k]) || 0), 0);
              const cost = ytd * (price || 0);
              return (
                <tr key={r.id}>
                  <td>
                    <input className="input" style={{ minWidth: 140 }} value={r.area_name ?? ""} onChange={(e) => setRows((rs) => rs.map((x) => x.id === r.id ? { ...x, area_name: e.target.value } : x))} onBlur={(e) => saveAreaName(r, e.target.value)} data-testid={`fuel-area-name-${r.id}`} />
                  </td>
                  {monthKeys.map((k) => {
                    const st = saveStatus[`${r.id}-${k}`];
                    return (
                      <td key={k} style={{ position: "relative" }}>
                        <input
                          className="input"
                          style={{ width: 72, padding: 4, paddingRight: 18,
                                   borderColor: st === "error" ? "var(--danger)" : st === "ok" ? "var(--accent)" : undefined }}
                          type="number"
                          step="any"
                          value={r[k] ?? 0}
                          onChange={(e) => applyCell(r.id, k, e.target.value)}
                          onBlur={(e) => persistCell(r, k, e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                          data-testid={`fuel-area-${r.id}-${k}`}
                        />
                        {st && <span style={{ position: "absolute", right: 4, top: "50%", transform: "translateY(-50%)", fontSize: 11 }}>{st === "saving" ? "⟳" : st === "ok" ? <span style={{ color: "var(--accent)" }}>✓</span> : <span style={{ color: "var(--danger)" }}>✗</span>}</span>}
                      </td>
                    );
                  })}
                  <td><b>{ytd.toFixed(0)}</b></td>
                  <td><b>${cost.toLocaleString("es-MX", { maximumFractionDigits: 2 })}</b></td>
                  <td><button className="btn btn-sm btn-danger" onClick={() => delArea(r)} title="Eliminar área" data-testid={`fuel-area-delete-${r.id}`}>🗑️</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {rows.length > 0 && (
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={chartData}>
            <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
            <XAxis dataKey="month" stroke="var(--text-dim)" fontSize={11} interval={0} />
            <YAxis stroke="var(--text-dim)" fontSize={11} />
            <Tooltip contentStyle={{ background: "var(--bg-2)", border: "1px solid var(--border-2)" }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {rows.map((r, i) => <Bar key={r.id} dataKey={r.area_name || r.id} fill={palette[i % palette.length]} />)}
          </BarChart>
        </ResponsiveContainer>
      )}
      {rows.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mt-3">
          {rows.map((r) => {
            const ytd = monthKeys.reduce((a, k) => a + (Number(r[k]) || 0), 0);
            const avg = ytd / Math.max(1, monthKeys.filter((k) => Number(r[k]) > 0).length);
            return (
              <div key={r.id} className="kpi-card" data-testid={`fuel-area-ytd-${r.id}`}>
                <div className="label">{r.area_name || "Sin nombre"}</div>
                <div className="value text-lg">{ytd.toFixed(0)} L</div>
                <div className="sub" style={{ fontSize: 10 }}>YTD {year} · Prom mensual: {avg.toFixed(0)} L · Costo: ${(ytd * (price || 0)).toLocaleString("es-MX", { maximumFractionDigits: 0 })}</div>
              </div>
            );
          })}
        </div>
      )}
      {rows.length > 0 && (
        <FuelSuggestions rows={rows} fuel={fuel} year={year} monthKeys={monthKeys} price={price} />
      )}
    </div>
  );
}

// ----- Air -----
function AirTab() {
  const [autoCO2, setAutoCO2] = useState(0);

  useEffect(() => {
    (async () => {
      // Auto CO2 from combustibles: read latest year's fuel_readings and compute.
      try {
        const { data } = await api.get("/data/fuel_readings");
        const year = new Date().getFullYear();
        const months = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];
        let total = 0;
        for (const r of data.filter((d) => d.year === year)) {
          const ms = months.reduce((a, m) => a + (Number(r[`volume_l_${m}`]) || 0), 0);
          total += ms * (r.fuel === "diesel" ? DIESEL_FACTOR : GASOLINE_FACTOR);
        }
        setAutoCO2(total);
      } catch { /* ignore */ }
    })();
  }, []);

  return (
    <div>
      <MonthlyReadings
        collection="air_emissions"
        title="Emisiones a la atmósfera (otras fuentes)"
        numericKeys={[
          { key: "co2eq_ton", label: "CO₂eq (TON)" },
        ]}
        extraColumns={[
          { key: "source", label: "Fuente", type: "text" },
          { key: "gas_type", label: "Tipo de gas", type: "text" },
          { key: "emission_factor", label: "Factor emisión", type: "number" },
        ]}
        headerCards={(_row, ytd) => {
          const other = ytd.co2eq_ton || 0;
          return [
            { label: "CO₂eq combustibles YTD", value: autoCO2.toFixed(2) + " TON", sub: "Auto desde Combustibles" },
            { label: "CO₂eq otras fuentes YTD", value: other.toFixed(2) + " TON" },
            { label: "Total CO₂eq YTD", value: (autoCO2 + other).toFixed(2) + " TON" },
          ];
        }}
        chart={{
          title: "Otras emisiones mensuales (TON CO₂eq)",
          series: [{ key: "co2eq_ton", label: "Otras fuentes", color: "#e74c3c" }],
        }}
      />
    </div>
  );
}

// ----- Soil -----
function SoilTab() {
  const PALETTE = ["#1d9e75", "#3aafa9", "#f1c40f", "#e67e22", "#9b59b6"];
  return (
    <div>
      <CrudTable
        collection="soil_records"
        testIdPrefix="soil"
        title="Registros de uso de suelo"
        fields={[
          { key: "record_date", label: "Fecha de registro", type: "date", required: true },
          { key: "total_area_m2", label: "Área total instalación (m²)", type: "number" },
          { key: "advc_area_m2", label: "Conservación/ADVC (m²)", type: "number" },
          { key: "vegetation_m2", label: "Cubierta vegetal (m²)", type: "number" },
          { key: "impermeable_m2", label: "Impermeabilizada (m²)", type: "number" },
          { key: "restoration_m2", label: "En restauración (m²)", type: "number" },
          { key: "observations", label: "Observaciones", type: "textarea", full: true },
        ]}
      />
      <SoilDonut palette={PALETTE} />
    </div>
  );
}

function SoilDonut({ palette }) {
  const [last, setLast] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/data/soil_records");
        if (data.length) setLast(data[0]); // sorted desc by created_at
      } catch { /* ignore */ }
    })();
  }, []);
  if (!last) return null;
  const data = [
    { name: "ADVC", value: last.advc_area_m2 || 0 },
    { name: "Cubierta vegetal", value: last.vegetation_m2 || 0 },
    { name: "Impermeabilizada", value: last.impermeable_m2 || 0 },
    { name: "En restauración", value: last.restoration_m2 || 0 },
  ].filter((d) => d.value > 0);
  if (!data.length) return null;
  return (
    <div className="card mt-4">
      <div className="card-title">Distribución del uso del suelo (último registro)</div>
      <ResponsiveContainer width="100%" height={260}>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" innerRadius={60} outerRadius={95} paddingAngle={2}>
            {data.map((_d, i) => <Cell key={i} fill={palette[i % palette.length]} />)}
          </Pie>
          <Tooltip contentStyle={{ background: "var(--bg-2)", border: "1px solid var(--border-2)", borderRadius: 8 }} />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

// ----- Otro -----
function OtherIndicatorsTab() {
  const [selected, setSelected] = useState(null);
  return (
    <div>
      <CrudTable
        collection="other_indicators"
        testIdPrefix="other-ind"
        onRowClick={(row) => setSelected(row)}
        fields={[
          { key: "name", label: "Nombre del indicador", required: true, full: true },
          { key: "unit", label: "Unidad de medida", required: true },
          { key: "description", label: "Descripción", type: "textarea", full: true },
        ]}
      />
      <Modal open={!!selected} onClose={() => setSelected(null)} title={`Indicador — ${selected?.name || ""}`} size="xl" testId="other-ind-detail">
        {selected && (
          <MonthlyReadings
            collection="other_indicator_readings"
            title={`Lecturas mensuales (${selected.unit || ""})`}
            filters={{ indicator_id: selected.id }}
            numericKeys={[{ key: "value", label: selected.name || "Valor" }]}
            headerCards={(_row, ytd) => [
              { label: `${selected.name} YTD`, value: (ytd.value || 0).toLocaleString("es-MX") + " " + (selected.unit || "") },
            ]}
            chart={{
              title: "Lecturas mensuales",
              series: [{ key: "value", label: selected.name, color: "#3498db" }],
            }}
          />
        )}
      </Modal>
    </div>
  );
}



// ----- BLOQUE 10 — Biodiversidad y Suelos -----
function BiodiversityTab() {
  const [sub, setSub] = useState("remediation");
  return (
    <div data-testid="biodiversity-tab">
      <div className="tabs-bar">
        <button className={`tab ${sub === "remediation" ? "active" : ""}`} onClick={() => setSub("remediation")} data-testid="bio-sub-remediation">Remediación de Suelos</button>
        <button className={`tab ${sub === "reforestation" ? "active" : ""}`} onClick={() => setSub("reforestation")} data-testid="bio-sub-reforestation">Reforestación</button>
        <button className={`tab ${sub === "trees" ? "active" : ""}`} onClick={() => setSub("trees")} data-testid="bio-sub-trees">Rescate y Movilización de Árboles</button>
        <button className={`tab ${sub === "fauna" ? "active" : ""}`} onClick={() => setSub("fauna")} data-testid="bio-sub-fauna">Atención a Fauna</button>
      </div>
      {sub === "remediation" && (
        <CrudTable collection="soil_remediation" testIdPrefix="soil-remediation" title="Remediación de Suelos" fields={[
          { key: "affected_area_m2", label: "Área afectada (m²)", type: "number" },
          { key: "contaminant", label: "Tipo de contaminante" },
          { key: "technique", label: "Técnica de remediación" },
          { key: "start_date", label: "Fecha inicio", type: "date" },
          { key: "end_date", label: "Fecha fin estimada", type: "date" },
          { key: "status", label: "Status", type: "select", options: ["Planeado", "En proceso", "Completado"] },
          { key: "cost_mxp", label: "Costo ($MXP)", type: "number" },
          { key: "observations", label: "Observaciones", type: "textarea", full: true },
        ]} />
      )}
      {sub === "reforestation" && (
        <CrudTable collection="reforestation" testIdPrefix="reforestation" title="Reforestación" fields={[
          { key: "species", label: "Especie plantada", required: true },
          { key: "plant_count", label: "Número de plantas", type: "number" },
          { key: "area_m2", label: "Área reforestada (m²)", type: "number" },
          { key: "date", label: "Fecha", type: "date" },
          { key: "location", label: "Ubicación" },
          { key: "survival_pct", label: "Sobrevivencia (%)", type: "number" },
          { key: "responsible", label: "Responsable" },
          { key: "observations", label: "Observaciones", type: "textarea", full: true },
        ]} />
      )}
      {sub === "trees" && (
        <CrudTable collection="tree_relocation" testIdPrefix="tree-relocation" title="Rescate y Movilización de Árboles" fields={[
          { key: "species", label: "Especie", required: true },
          { key: "count", label: "Número de individuos", type: "number" },
          { key: "origin", label: "Ubicación origen" },
          { key: "destination", label: "Punto de reubicación" },
          { key: "reason", label: "Motivo" },
          { key: "date", label: "Fecha", type: "date" },
          { key: "responsible", label: "Responsable" },
          { key: "observations", label: "Observaciones", type: "textarea", full: true },
        ]} />
      )}
      {sub === "fauna" && (
        <CrudTable collection="fauna_attention" testIdPrefix="fauna-attention" title="Atención a Fauna" fields={[
          { key: "species", label: "Especie", required: true },
          { key: "scientific_name", label: "Nombre científico" },
          { key: "date", label: "Fecha", type: "date" },
          { key: "state_on_rescue", label: "Estado al rescate", type: "select", options: ["Vivo y sano", "Vivo con lesiones", "Fallecido"] },
          { key: "action_taken", label: "Acción tomada", type: "textarea", full: true },
          { key: "final_destination", label: "Destino final" },
          { key: "responsible", label: "Responsable" },
        ]} />
      )}
    </div>
  );
}



// --- Item 3 — Materia Prima y Sustancias Químicas ---
// --- Item 11 — Sugerencias automáticas de reducción de consumo de combustible ---
function FuelSuggestions({ rows, fuel, year, monthKeys, price }) {
  const [show, setShow] = useState(false);
  if (!rows.length) return null;
  // Compute YTDs and statistics
  const areas = rows.map((r) => ({
    name: r.area_name || "Sin nombre",
    ytd: monthKeys.reduce((a, k) => a + (Number(r[k]) || 0), 0),
    monthly: monthKeys.map((k) => Number(r[k]) || 0),
  }));
  const totalYtd = areas.reduce((a, x) => a + x.ytd, 0);
  if (totalYtd === 0) return null;
  const top = [...areas].sort((a, b) => b.ytd - a.ytd)[0];
  const topShare = (top.ytd / totalYtd) * 100;
  const variability = (a) => {
    const nonZero = a.monthly.filter((x) => x > 0);
    if (nonZero.length < 2) return 0;
    const avg = nonZero.reduce((s, x) => s + x, 0) / nonZero.length;
    const v = Math.sqrt(nonZero.reduce((s, x) => s + (x - avg) ** 2, 0) / nonZero.length) / avg * 100;
    return v;
  };
  const tips = [];
  if (topShare > 40) tips.push(`🎯 El patio "${top.name}" concentra el ${topShare.toFixed(0)}% del consumo total. Concentrar acciones de eficiencia aquí maximiza el ahorro.`);
  const trend = areas.find((a) => a.monthly.slice(-3).some((x) => x > a.monthly.slice(0, 3).reduce((s, x2) => s + x2, 0) / 3 * 1.3));
  if (trend) tips.push(`📈 "${trend.name}" muestra tendencia creciente en los últimos meses. Revisa rendimiento de equipos y horas de operación.`);
  const var_high = areas.filter((a) => variability(a) > 50);
  if (var_high.length) tips.push(`⚠️ ${var_high.map((a) => `"${a.name}"`).join(", ")} presentan alta variabilidad (>50%). Considera estandarizar rutas y programación de equipos.`);
  tips.push(`💡 Implementa monitoreo telemático de equipos ${fuel === "diesel" ? "diésel" : "de gasolina"} para detectar tiempos muertos y rutas ineficientes.`);
  tips.push(`🔧 Programa mantenimiento preventivo: filtros de aire/combustible y afinación reducen consumo 5-12%.`);
  tips.push(`🚛 Optimiza la logística: consolidar viajes en ${top.name} podría reducir ${(top.ytd * 0.08).toFixed(0)} L/año (~${(top.ytd * 0.08 * (price || 0)).toLocaleString("es-MX", { maximumFractionDigits: 0 })} $MXP).`);
  tips.push(`🌱 Para equipos estacionarios (compresores, generadores) considera apagado automático en tiempos de baja demanda — ahorro típico 10-15%.`);
  return (
    <div className="card mt-3" style={{ background: "rgba(241,196,15,0.06)", border: "1px solid rgba(241,196,15,0.4)" }} data-testid={`fuel-suggestions-${fuel}`}>
      <div className="flex items-center justify-between">
        <div className="card-title text-sm" style={{ margin: 0 }}>💡 Sugerencias para reducir el consumo de {fuel === "diesel" ? "diésel" : "gasolina"} ({year})</div>
        <button className="btn btn-sm" onClick={() => setShow((s) => !s)} data-testid={`fuel-suggestions-toggle-${fuel}`}>
          {show ? "Ocultar" : "Ver sugerencias"}
        </button>
      </div>
      {show && (
        <ul style={{ paddingLeft: 18, marginTop: 8, fontSize: 13, lineHeight: 1.6 }}>
          {tips.map((t, i) => <li key={i}>{t}</li>)}
        </ul>
      )}
    </div>
  );
}

function RawMaterialsTab() {
  return (
    <CrudTable
      collection="raw_material_consumption"
      testIdPrefix="raw-materials"
      title="Consumo de Materia Prima (Acero, Soldadura, etc.)"
      fields={[
        { key: "date", label: "Fecha", type: "date", required: true },
        { key: "material", label: "Material", required: true },
        { key: "specification", label: "Especificación / Calibre / Tipo" },
        { key: "quantity", label: "Cantidad", type: "number", required: true },
        { key: "unit", label: "Unidad", type: "select", options: ["kg","ton","m","m²","m³","pza","rollo","caja"] },
        { key: "area", label: "Área / Patio / Proyecto" },
        { key: "supplier", label: "Proveedor" },
        { key: "cost_mxp", label: "Costo $MXP", type: "number" },
        { key: "po_number", label: "# Orden / Folio" },
        { key: "observations", label: "Observaciones", type: "textarea", full: true },
      ]}
    />
  );
}

function ChemicalsTab() {
  return (
    <CrudTable
      collection="chemical_substance_consumption"
      testIdPrefix="chemicals"
      title="Consumo de Sustancias Químicas (Pinturas, Solventes, etc.)"
      fields={[
        { key: "date", label: "Fecha", type: "date", required: true },
        { key: "substance", label: "Sustancia", required: true },
        { key: "type", label: "Tipo", type: "select", options: ["Pintura","Solvente","Diluyente","Aceite","Grasa","Adhesivo","Resina","Tinta","Otro"] },
        { key: "quantity", label: "Cantidad", type: "number", required: true },
        { key: "unit", label: "Unidad", type: "select", options: ["L","ml","kg","gal","gr","pza"] },
        { key: "area", label: "Área / Patio / Proyecto" },
        { key: "supplier", label: "Proveedor" },
        { key: "msds_ref", label: "HDS de referencia" },
        { key: "cretib", label: "CRETIB", type: "select", options: ["—","C","R","E","T","I","B","Combinado"] },
        { key: "cost_mxp", label: "Costo $MXP", type: "number" },
        { key: "observations", label: "Observaciones", type: "textarea", full: true },
      ]}
    />
  );
}
